import {
  Building2,
  Calendar,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Mail,
  MapPin,
  Phone,
  Printer,
  User,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';

import {
  MedicalProvider,
  ProviderType,
} from '../../../services/types/treatment-types';
import { Text } from '../../ui/catalyst/text';
import { getSimpleTreatmentStatus, SimpleTreatmentStatus } from '../utils';

interface ProviderInfoDisplayProps {
  provider: MedicalProvider;
  topContent?: ReactNode;
}

const ProviderOverview = ({ provider }: { provider: MedicalProvider }) => {
  const currentStatus = getSimpleTreatmentStatus(provider.treatment_status);
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex items-start justify-between mb-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-xl font-semibold text-gray-900">
              {provider.name || 'Unknown Provider'}
            </h3>
            {provider.provider_type && (
              <span className="text-sm bg-blue-50 text-blue-700 px-2 py-1 rounded font-medium">
                {
                  {
                    [ProviderType.ORGANIZATION]: 'Organization',
                    [ProviderType.PRACTITIONER]: 'Practitioner',
                    [ProviderType.HOSPITAL]: 'Hospital',
                    [ProviderType.EMS]: 'EMS',
                    [ProviderType.URGENT_CARE]: 'Urgent Care',
                  }[provider.provider_type] ?? provider.provider_type
                }
              </span>
            )}
          </div>

          {provider.address && (
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
              <MapPin className="h-4 w-4" />
              <span>{provider.address}</span>
            </div>
          )}
        </div>
      </div>

      {/* Treatment Status */}
      {currentStatus === SimpleTreatmentStatus.TREATING && (
        <div className="border-t border-gray-100 pt-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Treatment Status</span>
            <span
              className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
                currentStatus === SimpleTreatmentStatus.TREATING
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              {currentStatus === SimpleTreatmentStatus.TREATING
                ? 'Still Treating'
                : 'No Longer Treating'}
            </span>
          </div>
        </div>
      )}

      {/* Records Request Date */}
      {provider.date_of_records_request && (
        <div className="border-t border-gray-100 pt-3 mt-3">
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-gray-400" />
            <span className="text-gray-500">Records Requested</span>
            <span className="font-medium text-gray-900">
              {new Date(provider.date_of_records_request).toLocaleDateString()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const ContactInfo = ({ provider }: { provider: MedicalProvider }) => {
  if (!provider.handles || provider.handles.length === 0) {
    return null;
  }

  const getIconForHandleType = (handleType: string) => {
    switch (handleType) {
      case 'PHONE':
        return <Phone className="h-4 w-4 text-gray-400" />;
      case 'FAX':
        return <Printer className="h-4 w-4 text-gray-400" />;
      case 'EMAIL':
        return <Mail className="h-4 w-4 text-gray-400" />;
      case 'MAILING_ADDRESS':
        return <MapPin className="h-4 w-4 text-gray-400" />;
      default:
        return <Phone className="h-4 w-4 text-gray-400" />;
    }
  };

  const formatHandleType = (handleType: string) => {
    switch (handleType) {
      case 'PHONE':
        return 'Phone';
      case 'FAX':
        return 'Fax';
      case 'EMAIL':
        return 'Email';
      case 'MAILING_ADDRESS':
        return 'Address';
      default:
        return handleType;
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <h4 className="font-semibold text-gray-900 mb-4">Contact Information</h4>
      <div className="space-y-3">
        {provider.handles.map((handle, index) => (
          <div key={handle.id || index} className="flex items-start gap-3">
            {getIconForHandleType(handle.handle_type)}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-500">
                  {formatHandleType(handle.handle_type)}
                </span>
                {handle.preferred && (
                  <span className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
                    Preferred
                  </span>
                )}
                {handle.name && (
                  <span className="text-xs text-gray-400">• {handle.name}</span>
                )}
              </div>
              <div className="text-sm text-gray-900 mt-0.5">{handle.value}</div>
              {handle.notes && (
                <div className="text-xs text-gray-500 mt-1">{handle.notes}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const PractitionersInfo = ({ provider }: { provider: MedicalProvider }) => {
  if (
    !provider.practitioner_names ||
    provider.practitioner_names.length === 0
  ) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <h4 className="font-semibold text-gray-900 mb-4">Practitioners</h4>
      <div className="space-y-2">
        {provider.practitioner_names.map((name, index) => (
          <div key={index} className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100">
              <User className="h-4 w-4 text-blue-600" />
            </div>
            <span className="font-medium text-gray-900">{name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const TreatmentDetails = ({ provider }: { provider: MedicalProvider }) => {
  const [showDetails, setShowDetails] = useState(false);

  if (!provider.treatment && !provider.description) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <button
        type="button"
        onClick={() => setShowDetails(!showDetails)}
        className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
      >
        {showDetails ? (
          <>
            <ChevronDown className="h-3 w-3" />
            Hide treatment details
          </>
        ) : (
          <>
            <ChevronRight className="h-3 w-3" />
            Show treatment details
          </>
        )}
      </button>

      {showDetails && (
        <div className="mt-3 space-y-3">
          {provider.description && (
            <div>
              <Text className="text-sm text-gray-500 mb-1">Description</Text>
              <Text className="text-sm text-gray-900">
                {provider.description}
              </Text>
            </div>
          )}

          {provider.treatment && (
            <div>
              <Text className="text-sm text-gray-500 mb-1">
                Treatment Provided
              </Text>
              <Text className="text-sm text-gray-900">
                {provider.treatment}
              </Text>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FallbackLeadsInfo = ({ provider }: { provider: MedicalProvider }) => {
  const [showLeads, setShowLeads] = useState(false);

  if (!provider.fallback_leads || provider.fallback_leads.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <button
        type="button"
        onClick={() => setShowLeads(!showLeads)}
        className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
      >
        {showLeads ? (
          <>
            <ChevronDown className="h-3 w-3" />
            Hide alternative contacts
          </>
        ) : (
          <>
            <ChevronRight className="h-3 w-3" />
            Show alternative contacts ({provider.fallback_leads.length})
          </>
        )}
      </button>

      {showLeads && (
        <div className="mt-3 space-y-3">
          {provider.inference_reasoning && (
            <div className="text-xs text-gray-600 bg-gray-50 rounded p-2">
              {provider.inference_reasoning}
            </div>
          )}

          {provider.fallback_leads.map((lead, index) => (
            <div key={index} className="border border-gray-100 rounded p-3">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="h-4 w-4 text-gray-400" />
                <span className="font-medium text-gray-900">
                  {lead.organization_name || lead.practitioner_name}
                </span>
              </div>

              {lead.address && (
                <div className="text-sm text-gray-600 mb-2">{lead.address}</div>
              )}

              {lead.handles && lead.handles.length > 0 && (
                <div className="space-y-1">
                  {lead.handles.slice(0, 2).map((handle, handleIndex) => (
                    <div key={handleIndex} className="flex items-center gap-2">
                      <span className="text-sm text-gray-700">
                        {handle.value}
                      </span>
                      {handle.sources && handle.sources.length > 0 && (
                        <div className="flex gap-1">
                          {handle.sources.map((source, sourceIndex) => (
                            <a
                              key={sourceIndex}
                              href={source.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-gray-400 hover:text-gray-600"
                              title={source.title || source.url}
                            >
                              <ExternalLink className="h-2.5 w-2.5" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const ProviderInfoDisplay = ({
  provider,
  topContent,
}: ProviderInfoDisplayProps) => {
  return (
    <div className="space-y-4">
      <ProviderOverview provider={provider} />
      {topContent}
      <ContactInfo provider={provider} />
      <PractitionersInfo provider={provider} />
      <TreatmentDetails provider={provider} />
      <FallbackLeadsInfo provider={provider} />
    </div>
  );
};
